import { useEffect, useMemo, useRef, useState } from 'react'
import { aggregateMatrix, aggregateMatrixDropdown, isMatrixDropdown, type MatrixDropdownDataPoint, isDontKnowValue, getQuestionValue, getQuestionValues, matrixDropdownCellDeclaration } from '../chart-aggregation'
import { formatLabel } from '@/lib/utils/formatting'
import {
  formatAssetReportLabel,
  type AssetReportContextItem,
} from '../asset-report-label'
import { CHART_LIMITS } from '../../../foundations/slide-limits'
import { heatFillRgb, heatText } from '../../../plots/heat-grid/style'
import { getScaleAxisLayout, type ScaleAxisLayout } from '../NumericChart/scale-axis-layout'
import { DivergingBar } from '../DivergingBar'
import { bandCaptionTextClass, divergingRowFromDistribution } from '../diverging'
import {
  compareTop2ThenAverage,
  distributionFromMatrixRow,
  distributionFromSegmentItems,
  orientedTopTwoAriaPhrase,
  orientedTopTwoPct,
  paintThreeBandRow,
  pooledDistributionFromSegments,
  readDeclaredRatingUnit,
  readPolarity,
  threeBandsFor,
} from './matrixThreeBand'
import { resolveScaleAnchors, sameScaleAnchors, type ScaleAnchors } from './scaleAnchors'
import { LaneMergedSections } from '../LaneMergedSections'
import {
  unfoldResponsesForLane,
  type MonadicLaneScopeFact,
} from '../../../lib/monadicLaneScope'

const AVERAGE_BAR_COLOR = 'var(--slide-chart-1)'

const CATEGORY_COLORS = [
  'var(--slide-chart-1)',
  'var(--slide-chart-2)',
  'var(--slide-chart-3)',
  'var(--slide-chart-4)',
  'var(--slide-chart-5)',
  'var(--slide-chart-6)',
  'var(--slide-chart-7)',
  'var(--slide-chart-8)',
  'var(--slide-chart-1)',
]

function getCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

function resolveCssVarValue(value: string, depth = 0): string {
  if (typeof window === 'undefined') return value
  if (depth > 6) return value
  const varMatch = value.match(/var\(\s*(--[^),\s]+)\s*(?:,\s*([^)]+))?\)/)
  if (!varMatch) return value
  const varName = varMatch[1]
  const fallback = varMatch[2]?.trim() || ''
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback
  if (!resolved) return value
  const next = value.replace(varMatch[0], resolved)
  return resolveCssVarValue(next, depth + 1)
}

function parseColor(color: string): { r: number; g: number; b: number } {
  const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
    const hue = ((h % 360) + 360) % 360
    const sat = Math.max(0, Math.min(100, s)) / 100
    const light = Math.max(0, Math.min(100, l)) / 100
    const c = (1 - Math.abs(2 * light - 1)) * sat
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
    const m = light - c / 2

    let r1 = 0
    let g1 = 0
    let b1 = 0

    if (hue < 60) {
      r1 = c
      g1 = x
    } else if (hue < 120) {
      r1 = x
      g1 = c
    } else if (hue < 180) {
      g1 = c
      b1 = x
    } else if (hue < 240) {
      g1 = x
      b1 = c
    } else if (hue < 300) {
      r1 = x
      b1 = c
    } else {
      r1 = c
      b1 = x
    }

    return {
      r: Math.round((r1 + m) * 255),
      g: Math.round((g1 + m) * 255),
      b: Math.round((b1 + m) * 255),
    }
  }

  const c = resolveCssVarValue(color).trim()
  if (c.startsWith('#')) {
    const hex = c.slice(1)
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex
    const n = parseInt(full, 16)
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }
  const rgbMatch = c.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i)
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) }
  }
  const hslMatch = c.match(/hsla?\s*\(\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%/i)
  if (hslMatch) {
    return hslToRgb(Number(hslMatch[1]), Number(hslMatch[2]), Number(hslMatch[3]))
  }
  // CSS custom properties in this app often use bare HSL tuples like "173 60% 48%"
  const bareHslMatch = c.match(/(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
  if (bareHslMatch) {
    return hslToRgb(Number(bareHslMatch[1]), Number(bareHslMatch[2]), Number(bareHslMatch[3]))
  }
  const tokenFallback = resolveCssVarValue(getCssVar('--slide-text-primary', 'var(--ds-text-primary)')).trim()
  if (tokenFallback && tokenFallback !== c) {
    return parseColor(tokenFallback)
  }
  return { r: 0, g: 0, b: 0 }
}

/**
 * A rating's place on its scale, 0-1, or null for no data. The cell paints
 * through the heat-grid ramp (plots plan Q): one ramp and the registry's 14%
 * floor on every heat surface, resolved with the default palette behind every
 * token, so a card whose theme tokens are not resolved (a gallery, a test
 * render) never paints black, as the old interpolation did when it could not
 * parse a token.
 */
function heatPosition(value: number, min: number = 1, max: number = 5): number | null {
  const range = max - min
  if (value === 0 || !Number.isFinite(value) || !Number.isFinite(range) || range === 0) {
    return null
  }
  return Math.max(0, Math.min(1, (value - min) / range))
}

const getHeatmapColor = (value: number, min: number = 1, max: number = 5): string =>
  heatFillRgb(heatPosition(value, min, max))

const getHeatmapTextColor = (value: number, min: number = 1, max: number = 5): string =>
  heatText(heatPosition(value, min, max))

function scaleToPercent(value: number, min: number, max: number, fallback = 0): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max)) return fallback
  const span = max - min
  if (!Number.isFinite(span) || span <= 0) return fallback
  const percent = ((value - min) / span) * 100
  if (!Number.isFinite(percent)) return fallback
  return Math.max(0, Math.min(100, percent))
}

function isLightColor(color: string): boolean {
  try {
    const { r, g, b } = parseColor(color)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return luminance > 0.55
  } catch {
    return false
  }
}

function formatMatrixColumnLabel(label: string): string {
  const raw = String(label || '').trim()
  if (!raw) return 'Option'
  const withoutId = raw.replace(/\s+id$/i, '').trim()
  if (/^ad$/i.test(withoutId)) return 'Ad'
  if (/^pair$/i.test(withoutId)) return 'Ad/LP Pair'
  return withoutId || raw
}

function hasMirroredColumnDistributions(
  columns: Array<{ count?: number; distributionItems?: Array<{ count?: number }> }>,
): boolean {
  if (columns.length < 2) return false
  const signature = (col: { count?: number; distributionItems?: Array<{ count?: number }> }) => {
    const total = Number(col.count || 0)
    const counts = [...(col.distributionItems || [])]
      .map((item) => Number(item?.count || 0))
      .sort((a, b) => b - a)
    return `${total}|${counts.join(',')}`
  }
  const signatures = columns.map(signature)
  return new Set(signatures).size === 1
}

// `aggregateMatrix` emits rows in the questionnaire's authored order, but the render
// paths below keep only CHART_LIMITS.matrixMaxRows of them and call what survives the
// "top" rows. Ranking by the row average before the cap makes that sentence true and
// puts the strongest rows first. A row with no numeric answers has no rank at all, so
// it sorts LAST rather than being treated as a zero.
function parseAverage(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value))
  return Number.isFinite(parsed) ? parsed : null
}

function compareAveragesDesc(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  return b - a
}

/**
 * The min / midpoint / max wording under a three-band track. Same markup and
 * responsive rules as the Likert scale-axis footer, so a narrow report column
 * stacks the midpoint instead of letting it collide with the endpoints.
 */
function ScaleAnchorRow({ anchors, layout }: { anchors: ScaleAnchors; layout: ScaleAxisLayout }) {
  return (
    <div className="relative" data-testid="matrix-scale-anchors">
      <div className="flex justify-between text-xs text-muted-foreground">
        <span className={layout.endpointClassName} title={anchors.minLabel}>{anchors.minLabel}</span>
        <span
          className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 ${layout.inlineMidpointClassName}`}
          title={anchors.midLabel}
        >
          {anchors.midLabel}
        </span>
        <span className={layout.endpointClassName} title={anchors.maxLabel}>{anchors.maxLabel}</span>
      </div>
      {layout.stackMidpoint && (
        <div className={`text-xs ${layout.stackedMidpointClassName}`}>{anchors.midLabel}</div>
      )}
    </div>
  )
}

function threeBandCaption(percent: number, fillColor: string) {
  if (percent <= 0) {
    return undefined
  }
  return (
    <span className={bandCaptionTextClass(fillColor)}>
      {`${percent}%`}
    </span>
  )
}

// A segmented row carries one average per segment instead of a single row average. Its
// rank is the pooled mean across segments, each weighted by the answers it actually got,
// which is the same number the non-segmented view would show for that row.
function segmentedRowAverage(rowData: any): number | null {
  const segments = Array.isArray(rowData?.segments) ? rowData.segments : []
  let weightedSum = 0
  let total = 0

  segments.forEach((segment: any) => {
    const average = parseAverage(segment?.average)
    const count = Number(segment?.total || 0)
    if (average === null || !(count > 0)) return
    weightedSum += average * count
    total += count
  })

  return total > 0 ? weightedSum / total : null
}

interface MatrixChartProps {
  question: any
  segmentQuestion?: any
  responses: any[]
  maxRows?: number
  maxCols?: number
  colors?: string[]
  assetContextItems?: Array<AssetReportContextItem>
  monadicLaneScope?: MonadicLaneScopeFact | null
  emptyLaneLabel?: string
}

/**
 * @deprecated 2026-10-01: legacy reader only. Do not use in new code; draw the shared
 * plots in `packages/deck-ui/src/plots` instead. Kept so old studies the figure service
 * refuses (and cards whose figure fails) still draw exactly as before.
 * Delete after 2027-01-01, together with the legacy fallback in QuestionCard.
 */
export function MatrixChart(props: MatrixChartProps) {
  const { question, responses, monadicLaneScope, assetContextItems, emptyLaneLabel } = props
  if (unfoldResponsesForLane(question, responses, monadicLaneScope).length > 0) {
    return (
      <LaneMergedSections
        question={question}
        responses={responses}
        monadicLaneScope={monadicLaneScope}
        assetContextItems={assetContextItems}
        testIdPrefix="matrix-chart"
        emptyLabel={emptyLaneLabel}
      >
        {(unfolded) => (
          <MatrixChartBody {...props} responses={unfolded} segmentQuestion={undefined} monadicLaneScope={null} />
        )}
      </LaneMergedSections>
    )
  }
  return <MatrixChartBody {...props} />
}

function MatrixChartBody({ question, segmentQuestion, responses, maxRows, maxCols, colors: colorsProp, assetContextItems }: MatrixChartProps) {
  const effectiveCategoryColors = colorsProp || CATEGORY_COLORS
  const effectiveAvgBarColor = colorsProp?.[0] || AVERAGE_BAR_COLOR
  const isSegmented = !!segmentQuestion
  const isDropdownMatrix = isMatrixDropdown(question)
  const formatChartLabel = (raw: string) =>
    formatLabel(formatAssetReportLabel(raw, assetContextItems))

  // Segment values are raw ids (e.g. `asset_ad_2`); segmentQuestion.choices
  // already carries the authored asset title when one exists (see
  // buildAssetSegmentQuestion), so prefer it over reformatting the id.
  const segmentLabels = useMemo(() => {
    const map = new Map<string, string>()
    for (const choice of segmentQuestion?.choices || []) {
      const value = String(choice?.value ?? '').trim()
      const text = typeof choice?.text === 'string' ? choice.text.trim() : ''
      if (value && text) map.set(value, text)
    }
    return map
  }, [segmentQuestion])
  const segmentLabel = (segmentName: string) => segmentLabels.get(segmentName) || formatChartLabel(segmentName)
  const rowLimit = maxRows ?? CHART_LIMITS.matrixMaxRows
  const colLimit = maxCols ?? CHART_LIMITS.matrixMaxCols

  // QS-58 — measure the chart's OWN width (container-relative, not viewport) so the
  // Likert average-bar scale-axis row can switch to a non-overlapping layout in the
  // narrow report panel while staying byte-identical at the wide deck/export width.
  // `undefined` until measured → treated as wide, so first paint / SSR match the
  // established layout. Mirrors NumericChart.tsx / DoughnutChart.tsx.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined)
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const measure = () => setContainerWidth(el.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => measure())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const scaleAxis = getScaleAxisLayout(containerWidth)

  // Aggregate matrixdropdown data (2D: rows × columns with ratings)
  const matrixDropdownResult = useMemo(() => {
    if (!isDropdownMatrix) return null
    return aggregateMatrixDropdown(question, responses)
  }, [question, responses, isDropdownMatrix])

  // Segmentation is currently only supported for simple matrix/rating-like values.
  // MatrixDropdown values are nested per row/column and need dedicated segmented UX.
  if (isDropdownMatrix && isSegmented) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
      >
        <svg className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm text-center">Segmentation is not available yet for matrix table questions.</span>
      </div>
    )
  }

  // Render 2D matrixdropdown questions (numeric heatmap or categorical distribution cells)
  if (isDropdownMatrix && matrixDropdownResult && matrixDropdownResult.data.length > 0) {
    // Limit rows and columns to prevent DOM explosion
    const matrixDropdownData = matrixDropdownResult.data.slice(0, rowLimit)
    const questionColumnDefs = (question.columns || []).map((col: any) => ({
      key: String(col?.name || col?.value || col),
      label: formatMatrixColumnLabel(String(col?.title || col?.text || col?.name || col?.value || col)),
      choices: Array.isArray(col?.choices) ? col.choices : [],
      source: col,
    }))
    const columnDefs = (questionColumnDefs.length > 0
      ? questionColumnDefs
      : (matrixDropdownData[0]?.columns || []).map((col: any) => ({
          key: String(col?.value || col?.name || ''),
          label: formatMatrixColumnLabel(String(col?.name || col?.value || '')),
          choices: [],
          source: undefined,
        }))).slice(0, colLimit)
    const hasMoreColumns = questionColumnDefs.length > columnDefs.length

    const allCells = matrixDropdownData.flatMap((row) => row.columns)
    // Only reserve the per-cell "N/A" line when the grid actually has skips; otherwise it
    // just pads dead space between every row. Reserving grid-wide (not per-cell) keeps the
    // cells vertically aligned whenever any skip is present.
    const hasAnySkip = allCells.some((col) => (col.skipCount ?? 0) > 0)
    const nonEmptyCells = allCells.filter((col) => col.kind !== 'empty')
    const hasCategoricalValues = nonEmptyCells.some((col) => col.kind === 'categorical')
    const hasNumericValues = nonEmptyCells.some((col) => col.kind === 'numeric')

    const numericScaleValues = columnDefs
      .flatMap((col: any) =>
        col.choices
          .map((choice: any) => choice?.value ?? choice)
          .filter((v: any) => !isDontKnowValue(v, matrixDropdownCellDeclaration(question, col.source)))
          .map((v: any) => Number(v))
          .filter((v: number) => Number.isFinite(v)),
      )
    const rateMin = numericScaleValues.length > 0 ? Math.min(...numericScaleValues) : 1
    const rateMax = numericScaleValues.length > 0 ? Math.max(...numericScaleValues) : 5

    const categoricalLegendByColumn = columnDefs.reduce((acc: Record<string, string[]>, col: any) => {
      const labels = col.choices
        .map((choice: any) => ({
          value: String(choice?.value ?? choice),
          label: String(choice?.text ?? choice?.value ?? choice),
        }))
        .filter((choice: any) => !isDontKnowValue(choice.value, matrixDropdownCellDeclaration(question, col.source)))
        .map((choice: any) => choice.label)

      acc[col.key] = Array.from(new Set(labels))
      return acc
    }, {})

    const getOrderedItems = (col: any) => {
      const preferredOrder = categoricalLegendByColumn[col.value] || []
      return [...(col.distributionItems || [])].sort((a, b) => {
        const idxA = preferredOrder.indexOf(a.label)
        const idxB = preferredOrder.indexOf(b.label)
        if (idxA !== -1 && idxB !== -1) return idxA - idxB
        if (idxA !== -1) return -1
        if (idxB !== -1) return 1
        return b.count - a.count
      })
    }

    const shouldUseSingleRowCategoricalView =
      hasCategoricalValues && matrixDropdownData.length === 1 && columnDefs.length > 0

    if (shouldUseSingleRowCategoricalView) {
      const row = matrixDropdownData[0]
      const categoricalColumns = columnDefs
        .map((colDef: any) => row.columns.find((candidate) => candidate.value === colDef.key))
        .filter((col: any) => col && col.kind === 'categorical')
      const mirroredColumns = hasMirroredColumnDistributions(categoricalColumns)

      return (
        <div className="w-full space-y-6">
          {columnDefs.length > 1 && (
            <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              This question captures one selection per column (for example ad-level and pair-level priority).
              {mirroredColumns && ' These column distributions are currently identical, which usually indicates tightly coupled choices in this run.'}
            </div>
          )}
          {hasMoreColumns && (
            <div className="text-xs text-muted-foreground/80">
              Showing first {columnDefs.length} of {questionColumnDefs.length} columns.
            </div>
          )}
          {columnDefs.map((colDef: any) => {
            const col = row.columns.find((candidate) => candidate.value === colDef.key)
            if (!col) return null

            const orderedItems = getOrderedItems(col)
            const total = col.count || 0
            const maxItems = CHART_LIMITS.horizontalBarMaxItems
            const clippedItems = orderedItems.slice(0, maxItems)
            const hiddenItems = orderedItems.slice(maxItems)
            const hiddenCount = hiddenItems.reduce((sum: number, item: any) => sum + item.count, 0)

            return (
              <div key={colDef.key} className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground">{formatChartLabel(colDef.label)}</h4>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    n={total}
                    {col.skipCount > 0 ? ` • skip ${col.skipCount}` : ''}
                  </span>
                </div>

                <div className="space-y-2">
                  {clippedItems.map((item: any, idx: number) => {
                    const percent = total > 0 ? Math.round((item.count / total) * 100) : 0
                    return (
                      <div key={`${colDef.key}-${item.value}`} className="space-y-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[14px] font-normal text-foreground truncate" title={item.label}>
                            {formatChartLabel(item.label)}
                          </span>
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                            {percent}% • {item.count}
                          </span>
                        </div>
                        <div className="relative h-2.5 rounded bg-muted/35 overflow-hidden">
                          <div
                            className="absolute left-0 top-0 h-full rounded"
                            style={{
                              width: `${percent}%`,
                              backgroundColor: effectiveCategoryColors[idx % effectiveCategoryColors.length],
                            }}
                          />
                        </div>
                      </div>
                    )
                  })}

                  {hiddenItems.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {hiddenItems.length} more options grouped outside view ({hiddenCount} responses).
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    // Rows × a single numeric rating column → three-band rows. Multiple rows with
    // 3+ columns keep the heatmap below byte-identical (C 4.3 gate). A single row
    // has no "rows" for the heatmap to compare, so 2+ numeric columns transpose
    // instead: one three-band bar per column, reusing the same diverging renderer.
    const isSingleNumericRatingColumn =
      columnDefs.length === 1 && hasNumericValues && !hasCategoricalValues
    const isSingleRowNumericRatingColumns =
      !isSingleNumericRatingColumn &&
      matrixDropdownData.length === 1 &&
      columnDefs.length > 1 &&
      hasNumericValues &&
      !hasCategoricalValues

    if (isSingleNumericRatingColumn || isSingleRowNumericRatingColumns) {
      const polarity = readPolarity(question)

      interface ThreeBandRowView {
        key: string
        label: string
        average: string | null
        diverging: ReturnType<typeof divergingRowFromDistribution>
        bands: ReturnType<typeof threeBandsFor>
        anchors: ScaleAnchors
      }

      const buildThreeBandRow = (key: string, label: string, col: any, colDef: any): ThreeBandRowView => {
        const distribution: Record<number, number> = {}
        ;(col?.distributionItems || []).forEach((item: any) => {
          const value = Number(item.value)
          const count = Number(item.count) || 0
          if (Number.isFinite(value) && count > 0) {
            distribution[value] = (distribution[value] || 0) + count
          }
        })
        const bands = threeBandsFor(readDeclaredRatingUnit(question, colDef?.key), rateMin, rateMax)
        const diverging = divergingRowFromDistribution(distribution, rateMin, rateMax, bands)
        return {
          key,
          label,
          average: col?.avgRating != null ? col.avgRating.toFixed(1) : null,
          diverging,
          bands,
          anchors: resolveScaleAnchors(colDef?.source, rateMin, rateMax),
        }
      }

      // Column transpose keeps authored column order: these are distinct metrics
      // (e.g. feel / consider / buy), not rankable rows, so top-two-box sorting
      // would scramble a reading order the questionnaire author chose on purpose.
      const threeBandRows: Array<ThreeBandRowView> = isSingleNumericRatingColumn
        ? [...matrixDropdownData]
            .map((row) => {
              const colDef = columnDefs[0]
              const col = row.columns.find((candidate) => candidate.value === colDef.key)
              return buildThreeBandRow(row.rowValue, row.rowName, col, colDef)
            })
            .sort((a, b) =>
              compareTop2ThenAverage(
                { top2Pct: orientedTopTwoPct(a.diverging, polarity), average: parseAverage(a.average) },
                { top2Pct: orientedTopTwoPct(b.diverging, polarity), average: parseAverage(b.average) },
              ),
            )
        : columnDefs.map((colDef: any) => {
            const row = matrixDropdownData[0]
            const col = row.columns.find((candidate) => candidate.value === colDef.key)
            return buildThreeBandRow(colDef.key, colDef.label, col, colDef)
          })

      // One shared footer when every track reads on the same worded scale (the
      // rows-by-one-measure case), a per-row axis when the transpose stacked
      // distinct measures, each with its own anchors, on top of each other.
      const anchorsAreShared =
        threeBandRows.length > 0 &&
        threeBandRows.every((row) => sameScaleAnchors(row.anchors, threeBandRows[0].anchors))

      return (
        <div ref={rootRef} className="w-full space-y-6">
          {hasMoreColumns && (
            <div className="text-xs text-muted-foreground/80">
              Showing first {columnDefs.length} of {questionColumnDefs.length} columns.
            </div>
          )}
          <div className="space-y-6">
            {threeBandRows.map((row) => {
              const paints = row.diverging
                ? paintThreeBandRow(row.diverging, rateMin, rateMax, polarity, row.bands)
                : []
              return (
                <div
                  key={row.key}
                  className="space-y-2"
                  data-testid="matrix-three-band-row"
                  data-good-pole={polarity === 'lower_is_better' ? 'low' : polarity === 'middle_is_best' ? 'middle' : 'high'}
                >
                  <div className="flex items-center justify-between" title={row.label}>
                    <span className="text-sm font-medium">{formatChartLabel(row.label)}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="relative h-8">
                        {paints.length > 0 && (
                          <DivergingBar
                            className="h-8"
                            segments={paints.map((band) => ({
                              leftPct: band.leftPct,
                              widthPct: band.widthPct,
                              color: band.color,
                              value: `${band.percent}%`,
                              label: band.label,
                              caption: threeBandCaption(band.percent, band.color),
                              'data-testid': `matrix-three-band-${band.key}`,
                            }))}
                            ariaLabel={`${formatChartLabel(row.label)}: ${orientedTopTwoAriaPhrase(row.diverging, polarity)}, average ${row.average ?? 'n/a'}`}
                            data-testid="matrix-three-band"
                          />
                        )}
                      </div>
                      {!anchorsAreShared && (
                        <div className="mt-1">
                          <ScaleAnchorRow anchors={row.anchors} layout={scaleAxis} />
                        </div>
                      )}
                    </div>
                    <div className="w-12 shrink-0 text-right">
                      <span className="text-lg font-semibold tabular-nums">{row.average || '-'}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {anchorsAreShared && (
            <div className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <ScaleAnchorRow anchors={threeBandRows[0].anchors} layout={scaleAxis} />
              </div>
              <div className="w-12 shrink-0" />
            </div>
          )}
        </div>
      )
    }
    
    return (
      <div className="w-full space-y-4">
        {hasMoreColumns && (
          <div className="text-xs text-muted-foreground/80">
            Showing first {columnDefs.length} of {questionColumnDefs.length} columns.
          </div>
        )}
        {/* Header row with column labels */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-fixed">
            <colgroup>
              <col style={{ width: '28%' }} />
              {columnDefs.map((colDef: any) => (
                <col key={colDef.key} style={{ width: `${72 / columnDefs.length}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  className="text-left p-2 text-sm font-medium text-muted-foreground"
                >
                  {/* Empty corner cell */}
                </th>
                {columnDefs.map((colDef: any) => (
                  <th
                    key={colDef.key}
                    className="p-2 text-xs font-medium text-muted-foreground text-center"
                  >
                    <div className="truncate" title={colDef.label}>{formatChartLabel(colDef.label)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixDropdownData.map((row: MatrixDropdownDataPoint) => (
                <tr key={row.rowValue} className="border-t border-border/30">
                  {/* Row label */}
                  <td
                    className="p-2 text-sm font-medium align-top"
                    title={row.rowName}
                  >
                    {formatChartLabel(row.rowName)}
                  </td>
                  {/* Rating cells - limited to max columns */}
                  {columnDefs.map((colDef: any) => {
                    const col = row.columns.find((candidate) => candidate.value === colDef.key)
                    if (!col) {
                      return (
                        <td key={colDef.key} className="p-1 text-center">
                          <div className="w-20 h-10 rounded-md bg-muted/25 border border-border/40 flex items-center justify-center">
                            <span className="text-xs text-muted-foreground">-</span>
                          </div>
                        </td>
                      )
                    }

                    const answeredOrSkipped = col.count + col.skipCount
                    const skipPct = col.skipCount > 0 && answeredOrSkipped > 0
                      ? Math.round((col.skipCount / answeredOrSkipped) * 100)
                      : 0
                    const hasHighSkipRate = skipPct > 20
                    
                    if (col.kind === 'categorical') {
                      const orderedItems = getOrderedItems(col)

                      return (
                        <td key={col.value} className="p-1 text-center align-top">
                          <div className="w-full max-w-[240px] min-h-[3.1rem] rounded-md border border-border/40 bg-muted/15 p-1.5 flex flex-col gap-1 mx-auto">
                            {col.count > 0 ? (
                              <>
                                <span className="text-[10px] text-muted-foreground tabular-nums">
                                  n={col.count}
                                </span>
                                <div className="h-1.5 w-full rounded overflow-hidden flex bg-muted/40">
                                  {orderedItems.map((item: any, itemIdx: number) => {
                                    const width = col.count > 0 ? (item.count / col.count) * 100 : 0
                                    if (width === 0) return null
                                    const preferredOrder = categoricalLegendByColumn[col.value] || []
                                    const orderedIdx = preferredOrder.indexOf(item.label)
                                    const colorIdx = orderedIdx >= 0 ? orderedIdx : itemIdx

                                    return (
                                      <div
                                        key={`${item.value}-${item.label}`}
                                        className="h-full"
                                        style={{
                                          width: `${width}%`,
                                          backgroundColor: effectiveCategoryColors[colorIdx % effectiveCategoryColors.length],
                                        }}
                                        title={`${item.label}: ${item.count} (${Math.round(width)}%)`}
                                      />
                                    )
                                  })}
                                </div>
                              </>
                            ) : (
                              <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground">-</div>
                            )}
                            {hasAnySkip && (
                              <span
                                className={`${hasHighSkipRate ? '' : 'text-muted-foreground/60'} block h-3 leading-3 text-[10px]`}
                                style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                              >
                                {col.skipCount > 0 ? `N/A ${col.skipCount}` : ''}
                              </span>
                            )}
                          </div>
                        </td>
                      )
                    }

                    return (
                      <td key={col.value} className="p-1 text-center align-top">
                        <div className="flex flex-col items-center gap-0.5">
                          <div
                            className="w-full max-w-[160px] h-10 rounded-md flex items-center justify-center transition-all hover:scale-105 cursor-default mx-auto"
                            style={{
                              backgroundColor: getHeatmapColor(col.avgRating || 0, rateMin, rateMax),
                            }}
                            title={`${formatChartLabel(row.rowName)} × ${formatChartLabel(col.name)}: ${(col.avgRating || 0).toFixed(1)} (n=${col.count}${col.skipCount > 0 ? `, ${col.skipCount} skipped` : ''})`}
                          >
                            <span
                              className="text-sm font-semibold tabular-nums"
                              style={{ color: getHeatmapTextColor(col.avgRating || 0, rateMin, rateMax) }}
                            >
                              {col.count > 0 && col.avgRating !== null ? col.avgRating.toFixed(1) : '-'}
                            </span>
                          </div>
                          {hasAnySkip && (
                            <span
                              className={`${hasHighSkipRate ? '' : 'text-muted-foreground/60'} block h-3 leading-3 text-[10px]`}
                              style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                            >
                              {col.skipCount > 0 ? `N/A ${col.skipCount}` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
            {hasCategoricalValues && (
              <tfoot>
                <tr className="border-t border-border/50">
                  <td className="p-2" />
                  {columnDefs.map((colDef: any) => {
                    const legendValues = categoricalLegendByColumn[colDef.key] || []
                    if (legendValues.length === 0) {
                      return <td key={colDef.key} className="p-2" />
                    }
                    const maxLegendItems = CHART_LIMITS.doughnutMaxSegments
                    const displayedLegendValues = legendValues.slice(0, maxLegendItems)
                    const hiddenLegendCount = Math.max(0, legendValues.length - displayedLegendValues.length)
                    return (
                      <td key={colDef.key} className="p-2 align-top">
                        <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                          {displayedLegendValues.map((label: string, idx: number) => (
                            <span key={`${colDef.key}-${label}`} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                              <span
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: effectiveCategoryColors[idx % effectiveCategoryColors.length] }}
                              />
                              {formatChartLabel(label)}
                            </span>
                          ))}
                          {hiddenLegendCount > 0 && (
                            <span className="text-xs text-muted-foreground/70">
                              +{hiddenLegendCount} more
                            </span>
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {hasNumericValues && (
          <div className="flex items-center justify-center gap-2 pt-3 border-t border-border/50">
            <span className="text-xs text-muted-foreground">{rateMin} (Low)</span>
            <div className="flex h-3 w-32 rounded overflow-hidden">
              {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                <div
                  key={i}
                  className="flex-1 h-full"
                  style={{ backgroundColor: getHeatmapColor(rateMin + pct * (rateMax - rateMin), rateMin, rateMax) }}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">{rateMax} (High)</span>
          </div>
        )}
      </div>
    )
  }

  // Get column definitions (value for matching, text for display) - defined first as other hooks depend on it
  const columnDefs = useMemo(() => {
    if (!question.columns) return []
    return question.columns.map((c: any) => ({
      value: String(c.value !== undefined ? c.value : c),
      numericValue: Number(c.value !== undefined ? c.value : c),
      text: c.text || c.value || c
    }))
  }, [question])

  // Non-segmented data with averages per row
  const matrixResult = useMemo(() => {
    if (isSegmented) return { data: [], skipCounts: {}, totalResponses: 0 }
    return aggregateMatrix(question, responses)
  }, [question, responses, isSegmented])

  const data = useMemo(() => {
    if (isSegmented) return []
    
    // Calculate average for each row
    return matrixResult.data.map((row: any) => {
      let sum = 0
      let count = 0
      
      // Iterate through columns to get counts
      columnDefs.forEach((col: any) => {
        const colValue = row[col.text] || row[col.value] || 0
        if (colValue > 0 && !isNaN(col.numericValue)) {
          sum += col.numericValue * colValue
          count += colValue
        }
      })
      
      return {
        ...row,
        average: count > 0 ? (sum / count).toFixed(1) : null,
        totalResponses: count,
        skipCount: matrixResult.skipCounts[row.name] || 0
      }
    })
  }, [matrixResult, isSegmented, columnDefs])

  // Detect if this is a Likert scale (numeric columns) or categorical matrix
  const isLikertScale = useMemo(() => {
    const numericValues = columnDefs
      .map((c: any) => c.numericValue)
      .filter((v: number) => !isNaN(v))
    // Consider it Likert if majority of columns are numeric
    return numericValues.length >= columnDefs.length * 0.5 && numericValues.length > 0
  }, [columnDefs])

  // Calculate scale range from columns (for Likert matrices only)
  const scaleRange = useMemo(() => {
    if (!isLikertScale) {
      return { min: 1, max: 5, minLabel: '1', maxLabel: '5' }
    }
    
    const numericValues = columnDefs
      .map((c: any) => c.numericValue)
      .filter((v: number) => !isNaN(v) && Number.isFinite(v))

    // Guard against empty values array
    if (numericValues.length === 0) {
      return { min: 1, max: 5, minLabel: '1', maxLabel: '5' }
    }

    const min = Math.min(...numericValues)
    const max = Math.max(...numericValues)
    
    // Get labels from first and last column
    const firstCol = columnDefs.find((c: any) => c.numericValue === min)
    const lastCol = columnDefs.find((c: any) => c.numericValue === max)
    
    return {
      min,
      max,
      minLabel: firstCol?.text || String(min),
      maxLabel: lastCol?.text || String(max)
    }
  }, [columnDefs, isLikertScale])

  // Calculate midpoint
  const scaleMidpoint = (scaleRange.min + scaleRange.max) / 2

  // Get row labels
  const rowLabels = useMemo(() => {
    if (!question.rows) return []
    return question.rows.map((r: any) => ({
      value: r.value !== undefined ? r.value : r,
      text: r.text || r.value || r
    }))
  }, [question])

  // Segmented data - for each row, calculate distributions per segment
  const segmentedData = useMemo(() => {
    if (!isSegmented || !question.rows) return []

    // Group responses by segment value
    const segmentGroups: Record<string, any[]> = {}
    
    responses.forEach(r => {
      const segmentValues = new Set(getQuestionValues(r, segmentQuestion).map(String))
      segmentValues.forEach((segmentStr) => {
        if (!segmentGroups[segmentStr]) {
          segmentGroups[segmentStr] = []
        }
        segmentGroups[segmentStr].push(r)
      })
    })

    // Calculate total count per segment and sort by count (descending)
    const segmentCounts: Record<string, number> = {}
    Object.entries(segmentGroups).forEach(([name, resps]) => {
      segmentCounts[name] = resps.length
    })
    
    // Sort by count and limit to top N segments
    const allSegmentNames = Object.keys(segmentGroups)
    const sortedSegmentNames = allSegmentNames.sort((a, b) => segmentCounts[b] - segmentCounts[a])
    const segmentNames = sortedSegmentNames.slice(0, CHART_LIMITS.matrixMaxSegments)
    const totalSegmentCount = allSegmentNames.length

    // For each row, compute distribution per segment
    return rowLabels.map((row: any) => {
      const rowData = {
        rowName: row.text,
        totalSegmentCount,
        segments: segmentNames.map(segmentName => {
          const segmentResponses = segmentGroups[segmentName]
          
          // Count values for this row within this segment
          const counts: Record<string, number> = {}
          let total = 0
          let skipCount = 0
          
          segmentResponses.forEach(r => {
            const matrixData = getQuestionValue(r, question)
            if (!matrixData || typeof matrixData !== 'object') return
            
            // Iterate over matrix data entries and find matching row
            // This is more flexible with key type mismatches
            Object.entries(matrixData).forEach(([dataRowKey, cellValue]) => {
              // Check if this data row matches our target row
              const rowValStr = String(row.value)
              const isMatch = dataRowKey === rowValStr || 
                              dataRowKey === row.value ||
                              dataRowKey === row.text
              
              if (isMatch) {
                // Check if it's a skip/N/A value
                if (cellValue === undefined || cellValue === null || isDontKnowValue(cellValue, question)) {
                  skipCount++
                } else {
                  const valStr = String(cellValue)
                  counts[valStr] = (counts[valStr] || 0) + 1
                  total++
                }
              }
            })
          })

          // Calculate average (for Likert scales)
          let sum = 0
          Object.entries(counts).forEach(([val, count]) => {
            const numVal = Number(val)
            if (!isNaN(numVal)) {
              sum += numVal * count
            }
          })
          const average = total > 0 ? (sum / total).toFixed(1) : null

          // Build distribution array for categorical matrices
          // Use columnDefs order for consistent coloring
          const distribution = columnDefs.map((col: any) => {
            const count = counts[col.value] || counts[col.text] || 0
            return {
              label: col.text,
              value: col.value,
              count
            }
          })

          return {
            segmentName,
            total,
            skipCount,
            average,
            distribution
          }
        })
      }
      return rowData
    })
  }, [question, segmentQuestion, responses, isSegmented, rowLabels, columnDefs])

  // Likert rows render the oriented top-two box first (top-2 when higher is better,
  // bottom-2 when reversed, the middle band when middle is best; average is the tiebreaker). The row cap
  // below keeps the true top N instead of whichever rows the questionnaire
  // happened to author first.
  // Categorical matrices keep their authored order on purpose: their columns are unordered
  // labels, so a row "average" is meaningless there and no other column carries a
  // defensible rank. Authored order is the only ordering that still means something.
  const polarity = readPolarity(question)
  // Canonical policy bands when the question declares its rating unit; the
  // visual range split otherwise.
  const declaredBands = useMemo(
    () => threeBandsFor(readDeclaredRatingUnit(question), scaleRange.min, scaleRange.max),
    [question, scaleRange],
  )

  const orderedData = useMemo(() => {
    if (!isLikertScale) return data
    return [...data]
      .map((row: any) => {
        const diverging = divergingRowFromDistribution(
          distributionFromMatrixRow(row, columnDefs),
          scaleRange.min,
          scaleRange.max,
          declaredBands,
        )
        return { ...row, diverging }
      })
      .sort((a: any, b: any) =>
        compareTop2ThenAverage(
          { top2Pct: orientedTopTwoPct(a.diverging, polarity), average: parseAverage(a?.average) },
          { top2Pct: orientedTopTwoPct(b.diverging, polarity), average: parseAverage(b?.average) },
        ),
      )
  }, [data, isLikertScale, columnDefs, scaleRange, polarity, declaredBands])

  const orderedSegmentedData = useMemo(() => {
    if (!isLikertScale) return segmentedData
    return [...segmentedData]
      .map((rowData: any) => {
        const pooled = divergingRowFromDistribution(
          pooledDistributionFromSegments(rowData.segments || []),
          scaleRange.min,
          scaleRange.max,
          declaredBands,
        )
        const segments = (rowData.segments || []).map((segment: any) => ({
          ...segment,
          diverging: divergingRowFromDistribution(
            distributionFromSegmentItems(segment.distribution || []),
            scaleRange.min,
            scaleRange.max,
            declaredBands,
          ),
        }))
        return { ...rowData, segments, pooled }
      })
      .sort((a: any, b: any) =>
        compareTop2ThenAverage(
          { top2Pct: orientedTopTwoPct(a.pooled, polarity), average: segmentedRowAverage(a) },
          { top2Pct: orientedTopTwoPct(b.pooled, polarity), average: segmentedRowAverage(b) },
        ),
      )
  }, [segmentedData, isLikertScale, scaleRange, polarity, declaredBands])

  // Check for empty data
  if (!isSegmented && (!data || data.length === 0)) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
      >
        <svg className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm">No data available</span>
      </div>
    )
  }

  if (isSegmented && segmentedData.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/20 rounded-lg border border-dashed border-border/60 min-h-48"
      >
        <svg className="h-8 w-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <span className="text-sm">No data available</span>
      </div>
    )
  }

  // Slice the ORDERED rows, never the raw ones: sorting after the cap would still drop
  // the true top rows.
  const displayedRows = orderedData.slice(0, CHART_LIMITS.matrixMaxRows)
  const hasMoreRows = orderedData.length > CHART_LIMITS.matrixMaxRows
  const displayedSegmentRows = orderedSegmentedData.slice(0, CHART_LIMITS.matrixMaxRows)
  const hasMoreSegmentRows = orderedSegmentedData.length > CHART_LIMITS.matrixMaxRows

  // Segmented View
  if (isSegmented) {
    // Categorical matrix with segmentation - show stacked bars per segment within each row
    if (!isLikertScale) {
      const visibleColumnDefs = columnDefs.slice(0, CHART_LIMITS.matrixMaxCols)
      const hiddenColumnDefs = columnDefs.slice(CHART_LIMITS.matrixMaxCols)
      const visibleValueSet = new Set(visibleColumnDefs.map((c: any) => String(c.value)))
      const hasHiddenColumns = hiddenColumnDefs.length > 0
      const otherColumnLabel = `Other (${hiddenColumnDefs.length})`
      
      return (
        /* No max-height here. This branch renders only in the report's survey-data
           tab, which scrolls as a page, and the row / column / segment caps above
           already bound the block. A clamp cut the tail off with no scrollbar. */
        <div
          className="w-full space-y-6 overflow-hidden pr-2"
        >
          {displayedSegmentRows.map((rowData: any) => (
            <div key={rowData.rowName} className="space-y-3">
              {/* Row Title */}
              <div className="text-base font-semibold text-foreground">{formatChartLabel(rowData.rowName)}</div>
              
              {/* Segment stacked bars for this row */}
              <div className="space-y-2 pl-2">
                {rowData.segments.map((segment: any) => {
                  const total = segment.total || 0
                  const skipCount = segment.skipCount || 0
                  const totalWithSkips = total + skipCount
                  const skipPct = skipCount > 0 && totalWithSkips > 0
                    ? Math.round((skipCount / totalWithSkips) * 100)
                    : 0
                  const hasHighSkipRate = skipPct > 20
                  const visibleItems = (segment.distribution || [])
                    .filter((item: any) => visibleValueSet.has(String(item.value)))
                  const visibleCount = visibleItems.reduce((sum: number, item: any) => sum + (item.count || 0), 0)
                  const otherCount = Math.max(0, total - visibleCount)
                  const stackedItems = otherCount > 0
                    ? [
                        ...visibleItems,
                        { label: otherColumnLabel, value: '__other__', count: otherCount },
                      ]
                    : visibleItems
                  
                  return (
                    <div key={segment.segmentName} className="space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <span
                          className="text-sm font-medium min-w-0 flex-1 leading-snug line-clamp-2 break-words"
                          title={segmentLabel(segment.segmentName)}
                        >
                          {segmentLabel(segment.segmentName)}
                        </span>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>{total}</span>
                          {skipCount > 0 && (
                            <span
                              className={hasHighSkipRate ? '' : 'text-muted-foreground/60'}
                              style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                            >
                              (skip {skipCount})
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Stacked bar for this segment */}
                      <div className="flex w-full rounded overflow-hidden gap-0.5 ml-0 h-5">
                        {stackedItems.map((item: any, colIdx: number) => {
                          const percent = total > 0 ? (item.count / total) * 100 : 0
                          if (percent === 0) return null
                          const isOther = item.value === '__other__'
                          return (
                            <div
                              key={`${item.value}-${item.label}`}
                              className="h-full first:rounded-l last:rounded-r relative transition-all hover:opacity-80 cursor-default min-w-0.5"
                              style={{
                                width: `${percent}%`,
                                backgroundColor: isOther ? 'var(--slide-text-muted)' : effectiveCategoryColors[colIdx % effectiveCategoryColors.length],
                              }}
                              title={`${item.label}: ${item.count} (${Math.round(percent)}%)`}
                            >
                              {percent > 15 && (
                                <div
                                  className="absolute inset-0 flex items-center justify-center font-medium text-xs"
                                  style={{ color: isLightColor(effectiveCategoryColors[colIdx % effectiveCategoryColors.length]) ? 'var(--slide-text-primary, #064E3B)' : '#ffffff' }}
                                >
                                  {Math.round(percent)}%
                                </div>
                              )}
                            </div>
                          )
                        })}
                        {stackedItems.length === 0 && (
                          <div className="h-full w-full bg-muted/30 rounded" />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Legend */}
          <div className="flex flex-wrap gap-3 justify-center pt-3 border-t border-border/50">
            {visibleColumnDefs.map((colDef: any, colIdx: number) => (
              <div key={colDef.value} className="flex items-center gap-1.5 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: effectiveCategoryColors[colIdx % effectiveCategoryColors.length] }}
                />
                <span className="text-muted-foreground font-medium">{formatChartLabel(colDef.text)}</span>
              </div>
            ))}
            {hasHiddenColumns && (
              <div className="flex items-center gap-1.5 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--slide-text-muted)' }} />
                <span className="text-muted-foreground font-medium">{otherColumnLabel}</span>
              </div>
            )}
          </div>
          {hasHiddenColumns && (
            <div className="text-center pt-1">
              <p className="text-xs text-muted-foreground/80">
                Collapsed {hiddenColumnDefs.length} response options into {otherColumnLabel}.
              </p>
            </div>
          )}

          {/* Segment limit indicator */}
          {segmentedData[0]?.totalSegmentCount > CHART_LIMITS.matrixMaxSegments && (
            <div className="text-center pt-2">
              <p className="text-sm text-muted-foreground italic">
                Showing top {CHART_LIMITS.matrixMaxSegments} of {segmentedData[0].totalSegmentCount} segments by respondent count.
              </p>
            </div>
          )}
          {hasMoreSegmentRows && (
            <div className="text-center pt-2">
              <p className="text-sm text-muted-foreground italic">
                {/* Categorical rows keep their authored order, so these are the FIRST rows, not the top ones. */}
                Showing first {CHART_LIMITS.matrixMaxRows} of {segmentedData.length} rows.
              </p>
            </div>
          )}
        </div>
      )
    }
    
    // Likert scale segmented view — three-band rows per segment (C 4.3)
    return (
      /* No max-height here. This branch renders only in the report's survey-data
         tab, which scrolls as a page, and the row / column / segment caps above
         already bound the block. A clamp cut the tail off with no scrollbar. */
      <div
        ref={rootRef}
        className="w-full space-y-6 overflow-hidden pr-2"
      >
        {displayedSegmentRows.map((rowData: any) => (
          <div
            key={rowData.rowName}
            className="space-y-3"
            data-good-pole={polarity === 'lower_is_better' ? 'low' : polarity === 'middle_is_best' ? 'middle' : 'high'}
          >
            {/* Row Title */}
              <div className="text-base font-semibold text-foreground">{formatChartLabel(rowData.rowName)}</div>
            
            {/* Segment three-band rows for this statement */}
            <div className="space-y-2 pl-2">
              {rowData.segments.map((segment: any) => {
                const skipCount = segment.skipCount || 0
                const totalWithSkips = segment.total + skipCount
                const skipPct = skipCount > 0 && totalWithSkips > 0
                  ? Math.round((skipCount / totalWithSkips) * 100)
                  : 0
                const hasHighSkipRate = skipPct > 20

                return (
                  <div key={segment.segmentName} className="flex items-center gap-3">
                    {/* Segment Label with skip indicator */}
                    <div className="w-48 min-w-0 shrink-0 flex flex-col">
                      <span
                        className="text-sm leading-snug line-clamp-2 break-words"
                        title={segmentLabel(segment.segmentName)}
                      >
                        {segmentLabel(segment.segmentName)}
                      </span>
                      {skipCount > 0 && (
                        <span
                          className={`text-xs ${hasHighSkipRate ? '' : 'text-muted-foreground/60'}`}
                          style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                        >
                          skip {skipCount}
                        </span>
                      )}
                    </div>

                    {/* Three-band diverging track (C 4.3) */}
                    <div className="flex-1 relative h-6">
                      {segment.diverging && (
                        <DivergingBar
                          className="h-6"
                          segments={paintThreeBandRow(
                            segment.diverging,
                            scaleRange.min,
                            scaleRange.max,
                            polarity,
                            declaredBands,
                          ).map((band) => ({
                            leftPct: band.leftPct,
                            widthPct: band.widthPct,
                            color: band.color,
                            value: `${band.percent}%`,
                            label: band.label,
                            caption: threeBandCaption(band.percent, band.color),
                            'data-testid': `matrix-three-band-${band.key}`,
                          }))}
                          ariaLabel={`${segmentLabel(segment.segmentName)}: ${orientedTopTwoAriaPhrase(segment.diverging, polarity)}, average ${segment.average ?? 'n/a'}`}
                          data-testid="matrix-three-band"
                        />
                      )}
                    </div>

                    {/* Average value */}
                    <div className="w-14 shrink-0 text-right">
                      <span className="text-base font-semibold tabular-nums">{segment.average || '-'}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {/* Scale labels */}
        <div className="flex items-center pl-52 pr-14">
          <div className="flex-1 relative">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span className={scaleAxis.endpointClassName} title={scaleRange.minLabel}>{scaleRange.minLabel}</span>
              <span className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 ${scaleAxis.inlineMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </span>
              <span className={scaleAxis.endpointClassName} title={scaleRange.maxLabel}>{scaleRange.maxLabel}</span>
            </div>
            {scaleAxis.stackMidpoint && (
              <div className={`text-xs ${scaleAxis.stackedMidpointClassName}`}>
                {scaleMidpoint.toFixed(1)}
              </div>
            )}
          </div>
        </div>

        {/* Segment limit indicator */}
        {segmentedData[0]?.totalSegmentCount > CHART_LIMITS.matrixMaxSegments && (
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground italic">
              Showing top {CHART_LIMITS.matrixMaxSegments} of {segmentedData[0].totalSegmentCount} segments by respondent count.
            </p>
          </div>
        )}
        {hasMoreSegmentRows && (
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground italic">
              Showing top {CHART_LIMITS.matrixMaxRows} of {segmentedData.length} rows.
            </p>
          </div>
        )}
      </div>
    )
  }

  // Non-Segmented View - Categorical Matrix (stacked bars showing distribution)
  if (!isLikertScale) {
    const visibleColumnDefs = columnDefs.slice(0, CHART_LIMITS.matrixMaxCols)
    const hiddenColumnDefs = columnDefs.slice(CHART_LIMITS.matrixMaxCols)
    const hasHiddenColumns = hiddenColumnDefs.length > 0
    const otherColumnLabel = `Other (${hiddenColumnDefs.length})`
    
    return (
      <div className="w-full space-y-4">
        {/* Stacked Bars for each row */}
        <div className="space-y-5">
          {displayedRows.map((row: any) => {
            const total = row.value || 0
            const answeredOrSkipped = total + row.skipCount
            const skipPct = row.skipCount > 0 && answeredOrSkipped > 0
              ? Math.round((row.skipCount / answeredOrSkipped) * 100)
              : 0
            const hasHighSkipRate = skipPct > 20
            
            return (
              <div key={row.name} className="space-y-2">
                {/* Row Label */}
                <div className="flex items-center justify-between">
                  <span
                    data-testid="matrix-row-label"
                    className="text-[14px] font-normal text-foreground"
                  >
                    {formatChartLabel(row.name)}
                  </span>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{total} responses</span>
                    {row.skipCount > 0 && (
                      <>
                        <span>•</span>
                        <span
                          className={hasHighSkipRate ? '' : 'text-muted-foreground/60'}
                          style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                        >
                          {row.skipCount} skipped
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Stacked Bar */}
                <div className="flex w-full rounded-lg overflow-hidden gap-0.5 h-6">
                  {[...visibleColumnDefs, ...(hasHiddenColumns ? [{ value: '__other__', text: otherColumnLabel }] : [])].map((colDef: any, colIdx: number) => {
                    const count = colDef.value === '__other__'
                      ? hiddenColumnDefs.reduce((sum: number, hiddenCol: any) => {
                          const hiddenCount = row[hiddenCol.text] || row[hiddenCol.value] || 0
                          return sum + hiddenCount
                        }, 0)
                      : (row[colDef.text] || row[colDef.value] || 0)
                    const percent = total > 0 ? (count / total) * 100 : 0
                    
                    if (percent === 0) return null
                    const isOther = colDef.value === '__other__'
                    
                    return (
                      <div
                        key={colDef.value}
                        className="h-full first:rounded-l-lg last:rounded-r-lg relative group transition-all hover:opacity-80 cursor-default min-w-0.5"
                        style={{
                          width: `${percent}%`,
                          backgroundColor: isOther ? 'var(--slide-text-muted)' : effectiveCategoryColors[colIdx % effectiveCategoryColors.length],
                        }}
                        title={`${formatChartLabel(colDef.text)}: ${count} (${Math.round(percent)}%)`}
                      >
                        {percent > 12 && (
                          <div
                            className="absolute inset-0 flex items-center justify-center font-medium text-xs"
                            style={{ color: isLightColor(effectiveCategoryColors[colIdx % effectiveCategoryColors.length]) ? 'var(--slide-text-primary, #064E3B)' : '#ffffff' }}
                          >
                            {Math.round(percent)}%
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 justify-center pt-3 border-t border-border/50">
          {visibleColumnDefs.map((colDef: any, colIdx: number) => (
            <div key={colDef.value} className="flex items-center gap-1.5 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: effectiveCategoryColors[colIdx % effectiveCategoryColors.length] }}
              />
              <span className="text-muted-foreground font-medium">{formatChartLabel(colDef.text)}</span>
            </div>
          ))}
          {hasHiddenColumns && (
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--slide-text-muted)' }} />
              <span className="text-muted-foreground font-medium">{otherColumnLabel}</span>
            </div>
          )}
        </div>
        {hasHiddenColumns && (
          <div className="text-center pt-1">
            <p className="text-xs text-muted-foreground/80">
              Collapsed {hiddenColumnDefs.length} response options into {otherColumnLabel}.
            </p>
          </div>
        )}
        {hasMoreRows && (
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground italic">
              {/* Categorical rows keep their authored order, so these are the FIRST rows, not the top ones. */}
              Showing first {CHART_LIMITS.matrixMaxRows} of {data.length} rows.
            </p>
          </div>
        )}
      </div>
    )
  }

  // Likert Scale View — three-band diverging rows (C 4.3). Scale-axis footer
  // still uses the existing QS-58 container measurement (rootRef / containerWidth).
  return (
    <div ref={rootRef} className="w-full space-y-6">
      {/* Three-band rows, sorted by the oriented top-two box */}
      <div className="space-y-6">
        {displayedRows.map((row: any) => {
          const answeredOrSkipped = row.value + row.skipCount
          const skipPct = row.skipCount > 0 && answeredOrSkipped > 0
            ? Math.round((row.skipCount / answeredOrSkipped) * 100)
            : 0
          const hasHighSkipRate = skipPct > 20
          const paints = row.diverging
            ? paintThreeBandRow(row.diverging, scaleRange.min, scaleRange.max, polarity, declaredBands)
            : []

          return (
            <div
              key={row.name}
              className="space-y-2"
              data-testid="matrix-three-band-row"
              data-good-pole={polarity === 'lower_is_better' ? 'low' : polarity === 'middle_is_best' ? 'middle' : 'high'}
            >
              {/* Row Label - on top with skip count */}
              <div className="flex items-center justify-between" title={row.name}>
                <span data-testid="matrix-row-label" className="text-sm font-medium">{formatChartLabel(row.name)}</span>
                {row.skipCount > 0 && (
                  <span
                    className={`text-xs ${hasHighSkipRate ? '' : 'text-muted-foreground/60'}`}
                    style={hasHighSkipRate ? { color: 'var(--ds-amber-text)' } : undefined}
                  >
                    {row.skipCount} skipped
                  </span>
                )}
              </div>

              {/* Bar row with value */}
              <div className="flex items-center gap-4">
                <div className="flex-1 relative h-8">
                  {paints.length > 0 && (
                    <DivergingBar
                      className="h-8"
                      segments={paints.map((band) => ({
                        leftPct: band.leftPct,
                        widthPct: band.widthPct,
                        color: band.color,
                        value: `${band.percent}%`,
                        label: band.label,
                        caption: threeBandCaption(band.percent, band.color),
                        'data-testid': `matrix-three-band-${band.key}`,
                      }))}
                      ariaLabel={`${formatChartLabel(row.name)}: ${orientedTopTwoAriaPhrase(row.diverging, polarity)}, average ${row.average ?? 'n/a'}`}
                      data-testid="matrix-three-band"
                    />
                  )}
                </div>

                {/* Average value */}
                <div className="w-12 shrink-0 text-right">
                  <span className="text-lg font-semibold tabular-nums">{row.average || '-'}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {hasMoreRows && (
        <div className="text-center pt-2">
          <p className="text-sm text-muted-foreground italic">
            Showing top {CHART_LIMITS.matrixMaxRows} of {data.length} rows.
          </p>
        </div>
      )}

      {/* Scale labels */}
      <div className="flex items-center pr-12">
        <div className="flex-1 relative">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span className={scaleAxis.endpointClassName} title={scaleRange.minLabel}>{scaleRange.minLabel}</span>
            <span className={`absolute left-1/2 -translate-x-1/2 text-muted-foreground/60 text-xs ${scaleAxis.inlineMidpointClassName}`}>
              Midpoint ({scaleMidpoint.toFixed(1)})
            </span>
            <span className={scaleAxis.endpointClassName} title={scaleRange.maxLabel}>{scaleRange.maxLabel}</span>
          </div>
          {scaleAxis.stackMidpoint && (
            <div className={`text-xs ${scaleAxis.stackedMidpointClassName}`}>
              Midpoint ({scaleMidpoint.toFixed(1)})
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
